<template>
  <div class="report-tables__wrapper">
    <GlSection
      v-if="loading"
      class="report-tables__empty"
    >
      <template #title />
      <template #body>
        <div class="report-tables__loader-wrapper">
          <gl-loader
            height="100%"
            spin-blue-view
          />
        </div>
      </template>
    </GlSection>
    <div v-else>
      <GlSection class="report-tables">
        <template #title>
          <ReportBlockHeader
            title="Transactions"
            type="full"
          />
        </template>

        <template #body>
          <TableControl />
          <SourcesTable
            v-if="showSofTable"
            :currencies="currenciesByDirection.incoming"
            :data="filteredSofSources"
            :essential="essential"
            :extra-pdf-params="extraPdfParams"
            :selected-token="selectedToken"
          />
          <SourcesTable
            v-if="showUofTable"
            :currencies="currenciesByDirection.outgoing"
            :data="filteredUofSources"
            :essential="essential"
            :extra-pdf-params="extraPdfParams"
            :selected-token="selectedToken"
          />
        </template>
        <template
          v-if="isMobile"
          #footer
        >
          <ReportBlockFooter type="full" />
        </template>
      </GlSection>

      <SectionBlock
        v-if="!filteredSofSources.length && !filteredUofSources.length"
        class="report-tables__empty"
      >
        <template #title />
        <template #body>
          <div class="report-tables__empty-error">
            <gl-icon
              :height="16"
              name="info"
              :width="16"
            />
            {{ noTxErrorText }}
          </div>
        </template>
        <template
          v-if="isMobile"
          #footer
        >
          <ReportBlockFooter type="full" />
        </template>
      </SectionBlock>
    </div>
  </div>
</template>

<script>
// Components
import GlLoader from '@/components/gl-loader'
import SectionBlock from '@/components/gl-section-block'
import GlSection from '@/components/gl-section'
import GlIcon from '@/components/gl-icon'
import SourcesTable from '@/pages/report/components/gl-report/table/SourcesTable'
import TableControl from '@/pages/report/components/gl-report/table/TableControl'
import ReportBlockHeader from '@/pages/report/components/section-components/ReportBlockHeader'
import ReportBlockFooter from '@/pages/report/components/section-components/ReportBlockFooter'
// Utils
import { filterSourceResultByEntityType } from '@/utils/report-data-formatter'
import { spamMessagesCombine } from '@/utils/text-formatter'
// Vuex
import { mapState, mapGetters } from 'vuex'
//mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

export default {
  components: {
    GlLoader,
    SectionBlock,
    GlIcon,
    SourcesTable,
    GlSection,
    TableControl,
    ReportBlockHeader,
    ReportBlockFooter,
  },
  mixins: [deviceWidthMixin],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    reportType: {
      type: String,
      default: 'address',
    },
    pureSources: {
      type: Object,
      default: () => ({}),
    },
    currenciesByDirection: {
      type: Object,
      default: () => ({}),
    },
    selectedToken: {
      type: Object,
      default: () => ({}),
    },
    essential: {
      type: Boolean,
      default: false,
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    extraPdfParams: {
      type: Object,
      default: () => ({}),
    },
    currentScoreErrorMessage: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState('analytics', ['coinData', 'coinType']),
    ...mapGetters('directions', [
      'getActivePartBar',
      'getActiveTypeEntityItem',
      'getActiveExposurePart',
      'getActiveExposureTable',
    ]),

    filteredSofSources() {
      const result =
        this.filterSourceResultByEntityType(
          this.pureSources?.incoming,
          this.activeTypeEntityItem,
          this.getActivePartBar.value,
        ) || []

      return result
    },
    filteredUofSources() {
      const result =
        this.filterSourceResultByEntityType(
          this.pureSources?.outgoing,
          this.activeTypeEntityItem,
          this.getActivePartBar.value,
        ) || []

      return result
    },
    showSofTable() {
      return this.exposureTableKey === 'incoming'
    },
    showUofTable() {
      return this.exposureTableKey === 'outgoing'
    },

    noTxErrorText() {
      let errorMessage = `There are no transactions on this ${
        this.reportType == 'tx' ? 'tx' : 'address'
      }`

      if (
        this.spamMessagesCombine({ meta: this.currentScoreErrorMessage.meta })
      ) {
        errorMessage = this.spamMessagesCombine({
          meta: this.currentScoreErrorMessage.meta,
        })
      }
      if (
        this.spamMessagesCombine({
          selfFunding: this.currentScoreErrorMessage.selfFunding,
        })
      ) {
        errorMessage = this.spamMessagesCombine({
          selfFunding: this.currentScoreErrorMessage.selfFunding,
        })
      }

      return errorMessage
    },
    exposureKey() {
      return this.getActiveExposurePart.value
    },
    exposureTableKey() {
      return this.getActiveExposureTable.value
    },
    activeTypeEntityItem() {
      return this.getActiveTypeEntityItem
    },
  },
  methods: { filterSourceResultByEntityType, spamMessagesCombine },
}
</script>
<style>
.report-tables__wrapper {
  min-height: 700px;
}

.report-tables__wrapper .gl-section-wrapper,
.report-tables__wrapper .gl-section-title {
  padding-left: 10px;
}

.report-tables__loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 24px 0;
}

.report-tables__empty,
.report-tables {
  margin-bottom: 24px;
}

.report-tables__empty-error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 24px;
  margin-bottom: 24px;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 0 16px;
  gap: 8px;
}

.report-tables__empty-error svg {
  min-width: 16px;
}

.report-tables__wrapper .gl-section-block__body-wrapper {
  padding: 4px 0;
}

@media (max-width: 768px) {
  .report-tables__wrapper {
    min-height: 400px;
  }
}
</style>
